import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { Megaphone, BarChart3, Vote, Eye, FileText, ExternalLink, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_OPTIONS = [
  { value: 'duyuru', label: 'Duyuru', color: 'bg-blue-100 text-blue-700' },
  { value: 'anket', label: 'Görüş Toplama', color: 'bg-amber-100 text-amber-700' },
  { value: 'oylama', label: 'Karar Oylaması', color: 'bg-green-100 text-green-700' },
];

export const UserAnnouncements = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const [announcements, setAnnouncements] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [filterType]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      const { data } = await axios.get(`${API_URL}/api/announcements?${params}`, { withCredentials: true });
      setAnnouncements(data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (annId, vote) => {
    try {
      await axios.post(`${API_URL}/api/announcements/${annId}/vote`, { vote }, { withCredentials: true });
      toast.success('Oyunuz kaydedildi!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Oy kullanılamadı');
    }
  };

  const markViewed = async (annId) => {
    try {
      await axios.get(`${API_URL}/api/announcements/${annId}`, { withCredentials: true });
    } catch (e) { /* silent */ }
  };

  const getTypeBadge = (type) => {
    const opt = TYPE_OPTIONS.find(t => t.value === type);
    return opt ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span> : type;
  };

  const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    const d = new Date(deadline);
    d.setDate(d.getDate() + 1);
    return new Date() > d;
  };

  if (loading) {
    return <PageLayout><div className="flex items-center justify-center min-h-[50vh]"><p className="text-[#595959]">Yükleniyor...</p></div></PageLayout>;
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" data-testid="user-announcements-title">
            Duyurular & Oylamalar
          </h1>
          <p className="text-sm text-[#595959] mt-1">Site duyuruları, anketler ve karar oylamaları</p>
        </div>

        {/* Filter */}
        <div className="mb-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[170px]" data-testid="user-filter-ann-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Tipler</SelectItem>
              {TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Announcements */}
        <div className="space-y-3">
          {announcements.length === 0 ? (
            <Card className="border border-[#E5E5E5]">
              <CardContent className="py-12 text-center">
                <Megaphone className="w-10 h-10 text-[#D0D0D0] mx-auto mb-3" />
                <p className="text-[#595959]">Henüz duyuru bulunmuyor</p>
              </CardContent>
            </Card>
          ) : announcements.map((ann) => {
            const isVotable = ann.announcement_type !== 'duyuru';
            const canVote = isVotable && ann.status === 'aktif' && !ann.has_voted && !isDeadlinePassed(ann.deadline);
            const deadlinePassed = isDeadlinePassed(ann.deadline);

            return (
              <Card key={ann.id} className="border border-[#E5E5E5]" data-testid={`user-ann-${ann.id}`}
                onClick={() => markViewed(ann.id)}>
                <CardContent className="p-4 sm:p-5">
                  {/* Header */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {getTypeBadge(ann.announcement_type)}
                    {ann.status === 'kapali' && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">Kapalı</span>}
                    {ann.deadline && (
                      <span className={`text-xs flex items-center gap-1 ${deadlinePassed ? 'text-red-500' : 'text-[#595959]'}`}>
                        <Clock className="w-3 h-3" /> Son: {ann.deadline}
                        {deadlinePassed && ' (Süre dolmuş)'}
                      </span>
                    )}
                    {ann.has_voted && (
                      <span className="text-xs flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="w-3 h-3" /> Oy kullandınız
                      </span>
                    )}
                  </div>

                  {/* Title & Description */}
                  <h3 className="font-semibold text-[#1A1A2E] text-base sm:text-lg mb-1">{ann.title}</h3>
                  <p className="text-sm text-[#595959] whitespace-pre-wrap mb-3">{ann.description}</p>

                  {/* Documents */}
                  {ann.documents && ann.documents.length > 0 && (
                    <div className="space-y-1 mb-3">
                      <p className="text-xs font-medium text-[#595959]">Ekli Belgeler:</p>
                      {ann.documents.map(doc => (
                        <a key={doc} href={`${API_URL}/api/announcements/files/${ann.business_id}/${doc}`}
                          target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 text-sm text-[#2563EB] hover:underline bg-[#F9F8F6] px-3 py-1.5 rounded"
                          data-testid={`ann-doc-${doc}`}>
                          <FileText className="w-4 h-4" />
                          <span className="truncate flex-1">{doc}</span>
                          <ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Voting Section */}
                  {isVotable && (
                    <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                      {/* Already voted */}
                      {ann.has_voted && (
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-sm text-[#595959]">Oyunuz: </p>
                          <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                            ann.user_vote === 'evet' ? 'bg-green-100 text-green-700' :
                            ann.user_vote === 'hayir' ? 'bg-red-100 text-red-700' :
                            ann.user_vote === 'cekimser' ? 'bg-gray-100 text-gray-600' :
                            'bg-blue-50 text-blue-700'
                          }`}>
                            {ann.user_vote === 'evet' ? 'Evet' : ann.user_vote === 'hayir' ? 'Hayır' : ann.user_vote === 'cekimser' ? 'Çekimser' : ann.user_vote}
                          </span>
                        </div>
                      )}

                      {/* Vote buttons - evet/hayir */}
                      {canVote && ann.vote_type === 'evet_hayir' && (
                        <div className="flex items-center gap-2 flex-wrap" data-testid={`vote-buttons-${ann.id}`}>
                          <p className="text-sm text-[#595959] w-full mb-1">Oyunuzu kullanın:</p>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1" onClick={() => handleVote(ann.id, 'evet')} data-testid={`vote-evet-${ann.id}`}>
                            Evet
                          </Button>
                          <Button size="sm" className="bg-red-600 hover:bg-red-700 gap-1" onClick={() => handleVote(ann.id, 'hayir')} data-testid={`vote-hayir-${ann.id}`}>
                            Hayır
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleVote(ann.id, 'cekimser')} data-testid={`vote-cekimser-${ann.id}`}>
                            Çekimser
                          </Button>
                        </div>
                      )}

                      {/* Vote buttons - coktan secmeli */}
                      {canVote && ann.vote_type === 'coktan_secmeli' && (
                        <div className="space-y-2" data-testid={`vote-options-${ann.id}`}>
                          <p className="text-sm text-[#595959]">Seçiminizi yapın:</p>
                          {ann.options.map((opt) => (
                            <Button key={opt} variant="outline" size="sm" className="w-full justify-start gap-2" onClick={() => handleVote(ann.id, opt)} data-testid={`vote-opt-${opt}`}>
                              {opt}
                            </Button>
                          ))}
                        </div>
                      )}

                      {/* Results - if sharing is on */}
                      {ann.results && ann.result_sharing && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-[#595959] mb-2">Anlık Sonuçlar ({ann.total_votes} oy):</p>
                          {ann.vote_type === 'evet_hayir' && (
                            <div className="space-y-1.5">
                              {['evet', 'hayir', 'cekimser'].map(k => {
                                const count = ann.results[k] || 0;
                                const pct = ann.total_votes > 0 ? (count / ann.total_votes * 100) : 0;
                                return (
                                  <div key={k} className="flex items-center gap-2">
                                    <span className="text-xs w-16">{k === 'evet' ? 'Evet' : k === 'hayir' ? 'Hayır' : 'Çekimser'}</span>
                                    <div className="flex-1 bg-[#F0F0F0] rounded-full h-2.5 overflow-hidden">
                                      <div className={`h-full rounded-full ${k === 'evet' ? 'bg-green-500' : k === 'hayir' ? 'bg-red-500' : 'bg-gray-400'}`}
                                        style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-xs w-14 text-right">{count} (%{Math.round(pct)})</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {ann.vote_type === 'coktan_secmeli' && (
                            <div className="space-y-1.5">
                              {ann.options.map(opt => {
                                const count = ann.results[opt] || 0;
                                const pct = ann.total_votes > 0 ? (count / ann.total_votes * 100) : 0;
                                return (
                                  <div key={opt} className="flex items-center gap-2">
                                    <span className="text-xs w-24 truncate">{opt}</span>
                                    <div className="flex-1 bg-[#F0F0F0] rounded-full h-2.5 overflow-hidden">
                                      <div className="h-full rounded-full bg-[#2563EB]" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-xs w-14 text-right">{count} (%{Math.round(pct)})</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-[10px] text-[#999] mt-3">{ann.created_by_name} tarafından paylaşıldı</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </PageLayout>
  );
};
